/**
 * The call view model: joining and leaving a huddle, my mute / video / screen / hand state (optimistic, then sent to
 * the HuddleDO), heartbeats, the participant grid with speaking indicators, the settings sheet (devices, input meter,
 * noise suppression, echo cancellation, auto gain, background, mirror, join defaults — persisted per person on this
 * device), and for meeting huddles (external) the lobby, moderation, the share link and the meeting chat.
 *
 * Where the huddle comes from is the `CallHost`: in the app, the workspace (`workspaceCallHost`: names from the
 * directory, snapshots from `huddle.*` events); on the public meeting page, MeetingViewModel's polls. Media is the
 * platform CallEngine. State machine and presentation rules: logic/huddle.
 */
import type { CaptionWord, Huddle, HuddleCaption, HuddleTheme, HuddleDrawOp, HuddleMeeting, HuddleSignal, HuddleTracks, HuddleTranscription, LobbyEntry, TranscriptionState, Meeting, MeetingChatMessage, Member, NotificationEvent, UpdateHuddleRequest } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import {
  CUSTOM_BACKGROUND,
  HEARTBEAT_MS,
  IDLE_CALL,
  backgroundOptions,
  callErrorMessage,
  callTiles,
  callTransition,
  canModerate,
  chatCursor,
  deviceOptions,
  gridColumns,
  isHost,
  levelBars,
  lobbyLabel,
  mergeChat,
  newMeetingRequest,
  parsePrefs,
  participant,
  prefsKey,
  resolveDevice,
  sameTracks,
  speakingIds,
  stageUserId,
  updateSpeaking,
  withDevice,
  RING_TIMEOUT_MS,
  DECLINED_NOTICE_MS,
  HUDDLE_REACTION_MS,
  activeReactions,
  nextReactionExpiry,
  ringDeclined,
  trackReactions,
  type ShownReaction,
  HUDDLE_EFFECT_MS,
  activeEffects,
  huddleTheme,
  nextEffectExpiry,
  themeOptions,
  trackEffects,
  type CallEffect,
  type ShownEffect,
  type ThemeOption,
  type ThemeSpec,
  incomingRing,
  ringCandidates,
  ringExpired,
  toggleRingPick,
  type IncomingRing,
  type RingCandidate,
  type BackgroundEffect,
  type BackgroundOption,
  type CallError,
  type CallEvent,
  type CallMachine,
  type CallTile,
  type DeviceKind,
  type DeviceOption,
  type HuddlePrefs,
  type NewMeetingForm,
  type NoiseSuppression,
  addCaption,
  applyDraw,
  drawColor,
  flatten,
  liveCaptions,
  nextCaptionExpiry,
  nextStrokeTick,
  pruneStrokes,
  recordingNotice,
  strokesHit,
  transcribing,
  MAX_RECORDING_MS,
  type CaptionLine,
  type LiveStroke,
  strokeOpacity,
} from "../logic/huddle.ts";
import { channelTitle } from "../logic/sidebar.ts";
import * as Transcription from "../logic/transcription.ts";
import { errorKind, type CallEngine, type CallEngineState, type Clock, type HuddleApi, type KeyValueStorage, type PickedFile, type SpeechChunk, type CaptionTiming, type Unsubscribe, type UploadTarget } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type HuddleDeps = { api: HuddleApi; engine: CallEngine };

/** Where a call lives: identity, storage, names, and the latest snapshot of a huddle from outside the view model. */
export interface CallHost {
  /** Workspace id (path parameter for HuddleApi). */
  readonly wsId: string;
  /** My participant id: my user id, or a meeting guest id. */
  meId(): string;
  /** Whose call preferences these are (storage key owner; stable across joins). */
  prefsId(): string;
  readonly storage: KeyValueStorage;
  readonly clock: Clock;
  /** Directory names/avatars for members (empty on the public meeting page; participants carry names there). */
  members(): Record<string, Member>;
  /** Title of the conversation a huddle runs in. */
  titleOf(channelId: string): { title: string; isDm: boolean };
  /** The newest snapshot the host knows of (events, polls), or undefined. */
  huddleIn(channelId: string): Huddle | undefined;
  /** Notifies when members, titles or snapshots may have changed. */
  subscribe(listener: () => void): Unsubscribe;
  /** Heartbeats are needed (false when the host's own polling keeps me present). */
  readonly heartbeats: boolean;
  /** Who is in a conversation ("Invite people" lists them). Absent = no ringing (the public meeting page). */
  conversationMemberIds?(channelId: string): Promise<string[]>;
  /** Uploads a recording (a link huddle's has purpose "huddle") → its file id. Absent = recordings can't be saved. */
  upload?(channelId: string, file: PickedFile): Promise<string>;
  /** Sends a drawing change over the realtime socket. Absent = no drawing (the public meeting page). */
  sendDraw?(channelId: string, huddleId: string, op: HuddleDrawOp): void;
}

/** The in-app host: the workspace's directory and cache (`huddle.*` events). */
export function workspaceCallHost(ws: WorkspaceViewModel): CallHost {
  return {
    wsId: ws.wsId,
    meId: () => ws.state.meId,
    prefsId: () => ws.state.meId,
    storage: ws.deps.storage,
    clock: ws.deps.clock,
    members: () => ws.state.cache?.members ?? {},
    titleOf(channelId) {
      const cache = ws.state.cache;
      const ch = cache?.channels[channelId];
      return { title: cache === undefined ? "" : channelTitle(cache, channelId), isDm: ch !== undefined && (ch.kind === "dm" || ch.kind === "group_dm") };
    },
    huddleIn: (channelId) => ws.state.cache?.huddles?.[channelId],
    subscribe: (l) => ws.subscribe(l),
    heartbeats: true,
    async conversationMemberIds(channelId) {
      const ch = ws.state.cache?.channels[channelId];
      if (ch?.dmUserIds !== undefined) return ch.dmUserIds;
      return (await ws.deps.api.listChannelMembers(ws.wsId, channelId)).members.map((m) => m.userId);
    },
    async upload(channelId, file) {
      const target: UploadTarget = channelId.startsWith("meeting:") ? { type: "huddle" } : { type: "channel", channelId };
      const stored = await ws.deps.data.uploadFile(ws.wsId, file, target).done;
      return stored.id;
    },
    sendDraw: (channelId, huddleId, op) => ws.sendFrame({ op: "huddle.draw", channelId, huddleId, draw: op }),
  };
}

export type CallMe = { muted: boolean; video: boolean; screen: boolean; hand: boolean };

export type { NewMeetingForm };

/** The "New external huddle" sheet while open. */
export type NewMeetingState = NewMeetingForm & {
  busy: boolean;
  error?: MessageDescriptor;
  /** The link, once created. */
  created?: Meeting;
};

export type HuddleBackgroundOption = BackgroundOption & { thumb?: string };

/** Bars of the settings input meter. */
export const LEVEL_BARS = 12;
/** Meeting chat polling while in a meeting huddle (the link page polls through its own state call). */
export const CHAT_POLL_MS = 3000;
const MAX_CHAT = 300;

export type HuddleViewState = {
  call: CallMachine;
  /** My huddle (latest snapshot) while in a call. */
  huddle?: Huddle;
  /** The conversation's title ("design", or the DM partner's name), or the meeting's title. */
  title: string;
  /** The title is a plain name (a DM, or a link huddle's title): shown without "#". */
  isDm: boolean;
  me: CallMe;
  tiles: CallTile[];
  /** Whose screen is on the stage. */
  stageUserId?: string;
  columns: number;
  speaking: string[];
  media?: "sfu" | "unavailable";
  /** Last error, until dismissed or the next join. */
  error?: MessageDescriptor;
  // ----- Meeting huddles (external) -----
  meeting?: HuddleMeeting;
  /** People waiting to be let in (members only). */
  lobby: LobbyEntry[];
  lobbyLabel?: MessageDescriptor;
  isHost: boolean;
  /** Participants I may mute or remove. */
  moderatable: string[];
  chat: MeetingChatMessage[];
  chatOpen: boolean;
  chatDraft: string;
  /** Messages since I last looked at the chat. */
  unreadChat: number;
  /** The share link once fetched. */
  shareUrl?: string;
  /** "New external huddle" (open while defined). */
  newMeeting?: NewMeetingState;
  // ----- Ringing -----
  /** "Invite people" in my huddle (open while defined). */
  invite?: HuddleInviteState;
  /** Someone is ringing me into a huddle: Join / Decline (Slack's incoming huddle). */
  incoming?: IncomingRing;
  /** "Ana declined": someone I rang turned it down (shown for DECLINED_NOTICE_MS). */
  declined?: { userId: string; name: string };
  // ----- Settings -----
  settingsOpen: boolean;
  prefs: HuddlePrefs;
  devices: Record<DeviceKind, DeviceOption[]>;
  /** Lit bars (0…LEVEL_BARS) of my microphone. */
  inputLevel: number;
  backgrounds: HuddleBackgroundOption[];
  supports: CallEngineState["supports"];
  /** VideoView sources. */
  preview?: string;
  localVideo?: string;
  localScreen?: string;
  remoteVideo: Record<string, string>;
  remoteScreen: Record<string, string>;
  // ----- Captions, recording, drawing (Slack huddle parity) -----
  /** I turned captions on. */
  captionsOn: boolean;
  /** My microphone is being transcribed (someone has captions on and I'm unmuted). */
  transcribing: boolean;
  /** Caption lines up now (only while my captions are on). */
  captions: CaptionLine[];
  /** The note under my captions: where they're made and why (only while my captions are on and the engine knows). */
  captionNote?: MessageDescriptor;
  /** Someone is recording: the notice ("Ana is recording this huddle") and whether it's me. */
  recording?: { notice: MessageDescriptor; mine: boolean; startedAt: number };
  /** My last recording being saved / saved / failed. */
  recordingSave?: "saving" | "saved" | "failed";
  /** Drawing on the shared screen is on for me. */
  drawing: boolean;
  drawTool: "pen" | "eraser";
  drawColor: string;
  /** Strokes on the stage now, with their opacity. */
  strokes: (LiveStroke & { opacity: number })[];
  // ----- Effects (Slack huddle effects) -----
  /** The call's theme, drawn behind the whole call for everyone (undefined = plain). */
  theme?: ThemeSpec;
  /** The theme picker (None + every theme; the current one selected). */
  themes: ThemeOption[];
  /** Emoji effects bursting across the call now (animate each `key` once; logic effectBurst gives the layout). */
  effects: CallEffect[];
};

const OFF: CallMe = { muted: false, video: false, screen: false, hand: false };

/** The "Invite people" picker. */
export type HuddleInviteState = {
  query: string;
  /** Still loading who is in the conversation. */
  loading: boolean;
  candidates: RingCandidate[];
  picked: string[];
  busy: boolean;
  error?: MessageDescriptor;
  /** How many people the last ring reached ("Calling 2 people…"); the picker stays open to ring more. */
  rang?: number;
};

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function callError(e: unknown): CallError {
  const k = errorKind(e);
  if (k === "forbidden") return "forbidden";
  if (k === "notFound") return "notFound";
  return "network";
}

export class HuddleViewModel extends ViewModel<HuddleViewState> {
  private readonly host: CallHost;
  private readonly deps: HuddleDeps;
  private lastLoud: Record<string, number> = {};
  /** Tracks last announced to the server. */
  private announced: HuddleTracks = {};
  private sharing = false;
  private lastEngineErrorAt = 0;
  /** My huddle has shown up in the host's snapshots (so its disappearance means it ended). */
  private seenInHost = false;
  private cancelHeartbeat: (() => void) | undefined;
  private cancelChatPoll: (() => void) | undefined;
  /** Version of the last snapshot my own update produced (older remote mutes don't apply to newer local choices). */
  private myVersion = 0;
  /** The pre-join step wants the preview (see holdPreview). */
  private previewHeld = false;
  /** "Invite people": who is in the conversation (loaded when the picker opens). */
  private inviteMemberIds: string[] = [];
  /** The incoming ring: when it reached me, whether its huddle was seen in the host, its timeout. */
  private ringShownAt = 0;
  private ringSeen = false;
  private cancelRingTimer: (() => void) | undefined;
  private cancelDeclinedTimer: (() => void) | undefined;
  /** Reactions in the call: every participant's latest, with when I first saw it (logic trackReactions). */
  private seenReactions: Record<string, ShownReaction> = {};
  /** The next snapshot is the first of this call (reactions already in it don't float). */
  private firstSnapshot = true;
  private cancelReactionTimer: (() => void) | undefined;
  /** My last reaction, shown before the server echoes it. */
  private myReaction: { emoji: string; shownAt: number } | undefined;
  /** Effects in the call: every participant's latest, with when I first saw it (logic trackEffects). */
  private seenEffects: Record<string, ShownEffect> = {};
  /** My last effect, shown before the server echoes it (the echo keeps its start and layout). */
  private myEffect: ShownEffect | undefined;
  private cancelEffectTimer: (() => void) | undefined;
  /** A theme I picked that the server hasn't confirmed yet. */
  private pendingTheme: HuddleTheme | undefined;
  /** How this server transcribes ("server" clips or on-device text), from the join response (older engines). */
  private transcription: HuddleTranscription = "client";
  /** The modes this server takes, on-device first (JoinHuddleResponse.transcriptionModes). */
  private offeredModes: HuddleTranscription[] = ["client"];
  /** The mode I last told the server my device uses. */
  private reportedTranscription: TranscriptionState | undefined;
  private transcriptionMode: HuddleTranscription | "off" = "off";
  private cancelCaptionTimer: (() => void) | undefined;
  private recordingStartedAt = 0;
  private engineRecording = false;
  private cancelRecordingLimit: (() => void) | undefined;
  private liveStrokes: LiveStroke[] = [];
  private cancelStrokeTimer: (() => void) | undefined;
  /** My stroke in progress: its id and points not sent yet. */
  private pen: { id: string; pending: [number, number][]; flushAt: number } | undefined;
  private strokeSeq = 0;

  constructor(host: CallHost, deps: HuddleDeps) {
    const prefs = parsePrefs(host.storage.get(prefsKey(host.prefsId())));
    const e = deps.engine.getSnapshot();
    super({
      call: IDLE_CALL,
      title: "",
      isDm: false,
      me: OFF,
      tiles: [],
      columns: 1,
      speaking: [],
      lobby: [],
      isHost: false,
      moderatable: [],
      chat: [],
      chatOpen: false,
      chatDraft: "",
      unreadChat: 0,
      settingsOpen: false,
      prefs,
      devices: { audioinput: [], videoinput: [], audiooutput: [] },
      inputLevel: 0,
      backgrounds: [],
      supports: e.supports,
      remoteVideo: {},
      remoteScreen: {},
      captionsOn: false,
      transcribing: false,
      captions: [],
      drawing: false,
      drawTool: "pen",
      drawColor: drawColor(host.meId()),
      strokes: [],
      themes: themeOptions(undefined),
      effects: [],
    });
    this.host = host;
    this.deps = deps;
    deps.engine.applyPrefs(prefs);
    this.onDispose(host.subscribe(() => this.onHost()));
    this.onDispose(host.subscribe(() => this.checkRing()));
    this.onDispose(() => this.cancelRingTimer?.());
    this.onDispose(() => this.cancelDeclinedTimer?.());
    this.onDispose(() => this.cancelReactionTimer?.());
    this.onDispose(() => this.cancelEffectTimer?.());
    this.onDispose(deps.engine.subscribe(() => this.onEngine()));
    const speech = deps.engine.onSpeech?.((c) => this.onSpeech(c));
    if (speech !== undefined) this.onDispose(speech);
    const capability = deps.engine.onTranscriptionCapability?.(() => this.syncTranscription());
    if (capability !== undefined) this.onDispose(capability);
    this.onDispose(() => {
      this.cancelCaptionTimer?.();
      this.cancelStrokeTimer?.();
      this.cancelRecordingLimit?.();
    });
    this.onDispose(() => {
      if (this.state.call.status !== "idle") void this.leave();
      this.cancelHeartbeat?.();
      this.cancelChatPoll?.();
    });
    this.onEngine();
  }

  private get wsId(): string {
    return this.host.wsId;
  }

  private get meId(): string {
    return this.host.meId();
  }

  /** The active huddle of a conversation, as the host knows it (header chip, sidebar). */
  huddleIn(channelId: string): Huddle | undefined {
    return this.host.huddleIn(channelId);
  }

  // ---------- Joining and leaving ----------

  /** Joins (or starts) the conversation's huddle; leaves another one first. Joining the current one is a no-op. */
  async join(channelId: string): Promise<void> {
    const current = this.state.call;
    if (current.status !== "idle") {
      if (current.channelId === channelId) return;
      await this.leave();
    }
    const prefs = this.state.prefs;
    const me: CallMe = { muted: prefs.joinWithMicOff, video: !prefs.joinWithCameraOff, screen: false, hand: false };
    this.dispatch({ type: "join", channelId });
    this.update({ me, error: undefined, chat: [], unreadChat: 0, shareUrl: undefined, ...this.host.titleOf(channelId) });
    let res;
    try {
      // A link huddle without a conversation (`meeting:<code>`) joins through its link (works with Chat off).
      const code = channelId.startsWith("meeting:") ? channelId.slice("meeting:".length) : undefined;
      res =
        code === undefined
          ? await this.deps.api.join(this.wsId, channelId, { muted: me.muted, video: me.video })
          : await this.deps.api.joinMeeting(this.wsId, code, { muted: me.muted, video: me.video });
    } catch (e) {
      if (this.state.call.status === "joining") this.dispatch({ type: "failed", error: callError(e) });
      return;
    }
    if (this.state.call.status !== "joining" || this.state.call.channelId !== channelId) {
      // Left (or switched) while the request was in flight.
      void this.deps.api.leave(this.wsId, res.huddle.id).catch(() => undefined);
      return;
    }
    this.announced = {};
    this.sharing = false;
    this.seenInHost = false;
    this.lastLoud = {};
    this.seenReactions = {};
    this.myReaction = undefined;
    this.seenEffects = {};
    this.myEffect = undefined;
    this.pendingTheme = undefined;
    this.firstSnapshot = true;
    this.myVersion = res.huddle.version;
    this.transcription = res.transcription ?? "client";
    this.offeredModes = Transcription.offeredModes(res.transcriptionModes, res.transcription);
    this.reportedTranscription = undefined;
    this.dispatch({ type: "joined", huddleId: res.huddle.id });
    this.update({ huddle: res.huddle, media: res.media.kind, drawColor: drawColor(this.meId) });
    this.deps.engine.connect({ ws: this.wsId, huddleId: res.huddle.id, media: res.media, mic: !me.muted, camera: me.video, prefs: this.state.prefs });
    this.deps.engine.setRemote(res.huddle.participants.filter((p) => p.userId !== this.meId));
    this.recompute();
    if (this.host.heartbeats) this.scheduleHeartbeat();
    this.scheduleChatPoll();
    this.onHost();
  }

  async leave(): Promise<void> {
    const { status, huddleId } = this.state.call;
    if (status === "idle" || status === "leaving") return;
    this.dispatch({ type: "leave" });
    if (this.state.recording?.mine === true) void this.finishRecording(true);
    this.teardown();
    if (huddleId !== undefined) await this.deps.api.leave(this.wsId, huddleId).catch(() => undefined);
    this.dispatch({ type: "left" });
    this.resetCall();
  }

  dismissError(): void {
    this.update({ error: undefined });
  }

  // ---------- My state ----------

  toggleMute(): void {
    if (!this.active()) return;
    const muted = !this.state.me.muted;
    this.setMe({ ...this.state.me, muted });
    this.deps.engine.setMic(!muted);
    this.send({ muted });
  }

  toggleVideo(): void {
    if (!this.active()) return;
    const video = !this.state.me.video;
    this.setMe({ ...this.state.me, video });
    this.deps.engine.setCamera(video);
    this.send({ video });
  }

  /** Screen sharing starts when the engine says so (the person may cancel the system picker). */
  toggleScreen(): void {
    if (!this.active()) return;
    const on = !this.state.me.screen;
    this.setMe({ ...this.state.me, screen: on });
    this.deps.engine.setScreen(on);
    if (!on) {
      this.sharing = false;
      this.send({ screen: false });
    }
  }

  toggleHand(): void {
    if (!this.active()) return;
    const hand = !this.state.me.hand;
    this.setMe({ ...this.state.me, hand });
    this.send({ hand });
  }

  /** Sends an emoji reaction to everyone in the call (Slack huddle reactions): it floats over my tile for a moment. */
  react(emoji: string): void {
    if (!this.active() || emoji.trim() === "" || /\s/.test(emoji)) return;
    const now = this.host.clock.now();
    // Shown at once; the server's copy keeps this start time when it comes back.
    this.myReaction = { emoji, shownAt: now };
    this.recompute();
    this.send({ reaction: emoji });
  }

  /** Sends an emoji effect (Slack huddle effects): it bursts across the whole call for everyone. */
  sendEffect(emoji: string): void {
    if (!this.active() || emoji.trim() === "" || /\s/.test(emoji)) return;
    const now = this.host.clock.now();
    this.myEffect = { emoji, at: now, shownAt: now, seed: now };
    this.recompute();
    this.send({ effect: emoji });
  }

  /** Changes the call's theme for everyone ("none" = plain). Shown at once; then whatever the server's answer says. */
  setTheme(id: HuddleTheme): void {
    if (!this.active()) return;
    if ((huddleTheme(this.pendingTheme ?? this.state.huddle?.theme)?.id ?? "none") === id) return;
    this.pendingTheme = id;
    this.recompute();
    this.send({ theme: id }, () => {
      if (this.pendingTheme !== id) return;
      this.pendingTheme = undefined;
      this.recompute();
    });
  }

  // ---------- Captions, recording, drawing ----------

  /** Live captions on/off for me (Slack's "Turn on captions"). */
  toggleCaptions(): void {
    if (!this.active()) return;
    const on = !this.state.captionsOn;
    this.update({ captionsOn: on, captions: on ? this.state.captions : [] });
    this.send({ captions: on });
    this.syncTranscription();
  }

  /** A live signal from the realtime socket (captions, drawing) — only the ones for my huddle are used. */
  onSignal(signal: HuddleSignal): void {
    const huddleId = this.state.call.huddleId;
    if (huddleId === undefined) return;
    if (signal.type === "huddle.caption") {
      if (signal.caption.huddleId === huddleId) this.showCaption(signal.caption);
      return;
    }
    if (signal.huddleId !== huddleId || signal.userId === this.meId) return;
    this.liveStrokes = applyDraw(this.liveStrokes, signal.userId, signal.op, this.host.clock.now());
    this.redrawStrokes();
  }

  /** Start or stop recording the call (consent: everyone sees the notice while it runs). */
  toggleRecording(): void {
    if (!this.active()) return;
    if (this.state.recording?.mine === true) {
      void this.finishRecording(false);
      return;
    }
    if (this.state.recording !== undefined) return;
    const start = this.deps.engine.startRecording;
    if (start === undefined || this.host.upload === undefined || !start.call(this.deps.engine)) {
      this.update({ error: msg("huddle.recording.unsupported") });
      return;
    }
    this.recordingStartedAt = this.host.clock.now();
    this.update({ recording: { notice: msg("huddle.recording.you"), mine: true, startedAt: this.recordingStartedAt }, recordingSave: undefined });
    this.send({ recording: true });
    this.cancelRecordingLimit?.();
    this.cancelRecordingLimit = this.host.clock.setTimer(MAX_RECORDING_MS, () => {
      this.cancelRecordingLimit = undefined;
      void this.finishRecording(false);
    });
  }

  toggleDrawing(): void {
    if (!this.active()) return;
    this.endStroke();
    this.update({ drawing: !this.state.drawing, drawTool: "pen" });
  }

  setDrawTool(tool: "pen" | "eraser"): void {
    this.endStroke();
    this.update({ drawTool: tool });
  }

  /** Pointer down on the shared screen (x, y as fractions of the picture). */
  drawStart(x: number, y: number): void {
    if (!this.state.drawing || !this.active()) return;
    if (this.state.drawTool === "eraser") {
      this.eraseAt(x, y);
      return;
    }
    const id = `${this.meId}:${this.host.clock.now()}:${++this.strokeSeq}`;
    this.pen = { id, pending: [[x, y]], flushAt: this.host.clock.now() };
    this.liveStrokes = applyDraw(this.liveStrokes, this.meId, { kind: "stroke", stroke: { id, color: this.state.drawColor, points: [x, y] } }, this.host.clock.now());
    this.redrawStrokes();
  }

  drawMove(x: number, y: number): void {
    if (!this.state.drawing) return;
    if (this.state.drawTool === "eraser") {
      this.eraseAt(x, y);
      return;
    }
    const pen = this.pen;
    if (pen === undefined) return;
    pen.pending.push([x, y]);
    const now = this.host.clock.now();
    this.liveStrokes = applyDraw(this.liveStrokes, this.meId, { kind: "stroke", stroke: { id: pen.id, color: this.state.drawColor, points: [x, y] } }, now);
    this.redrawStrokes();
    // Batched: about 20 frames a second, at most 64 points each.
    if (now - pen.flushAt >= 50 || pen.pending.length >= 64) this.flushPen(false);
  }

  drawEnd(): void {
    this.endStroke();
  }

  // ---------- Meeting huddles: lobby, moderation, link, chat ----------

  admit(guestId: string): void {
    this.decide({ guestIds: [guestId], admit: true });
  }

  deny(guestId: string): void {
    this.decide({ guestIds: [guestId], admit: false });
  }

  admitAll(): void {
    this.decide({ all: true, admit: true });
  }

  muteParticipant(userId: string): void {
    this.moderate({ action: "mute", userId });
  }

  removeParticipant(userId: string): void {
    this.moderate({ action: "remove", userId });
  }

  /** Host: ends the huddle for everyone. */
  endForAll(): void {
    this.moderate({ action: "end" });
  }

  /** "Share link": the huddle's meeting link (created on first use); resolves to its URL. */
  async shareLink(): Promise<string | undefined> {
    const huddleId = this.state.call.huddleId;
    if (huddleId === undefined) return undefined;
    try {
      const m = await this.deps.api.shareLink(this.wsId, huddleId);
      this.update({ shareUrl: m.url });
      return m.url;
    } catch (e) {
      this.update({ error: callErrorMessage(callError(e)) });
      return undefined;
    }
  }

  setMeetingLocked(locked: boolean): void {
    void this.updateMeeting({ locked });
  }

  /** "Anyone with the link can join directly" (open) vs ask to join. */
  setMeetingOpen(open: boolean): void {
    void this.updateMeeting({ policy: open ? "open" : "ask" });
  }

  openChat(): void {
    this.update({ chatOpen: true, unreadChat: 0 });
  }

  closeChat(): void {
    this.update({ chatOpen: false });
  }

  setChatDraft(text: string): void {
    this.update({ chatDraft: text });
  }

  sendChat(): void {
    const text = this.state.chatDraft.trim();
    const huddleId = this.state.call.huddleId;
    if (text === "" || huddleId === undefined) return;
    this.update({ chatDraft: "" });
    this.deps.api
      .postChat(this.wsId, huddleId, text)
      .then((m) => this.addChat([m], true))
      .catch((e: unknown) => this.update({ error: callErrorMessage(callError(e)), chatDraft: text }));
  }

  /** New chat from outside (the meeting page's polls). */
  receiveChat(messages: MeetingChatMessage[]): void {
    this.addChat(messages, false);
  }

  // ---------- Ringing people in ----------

  /** "Invite people" (in a huddle of a conversation; not on the public meeting page). */
  get canInvite(): boolean {
    return this.host.conversationMemberIds !== undefined && this.deps.api.ring !== undefined && this.state.call.huddleId !== undefined && this.state.meeting === undefined;
  }

  openInvite(): void {
    const channelId = this.state.call.channelId;
    const load = this.host.conversationMemberIds;
    if (channelId === undefined || load === undefined) return;
    this.inviteMemberIds = [];
    this.update({ invite: { query: "", loading: true, candidates: [], picked: [], busy: false } });
    load.call(this.host, channelId).then(
      (ids) => {
        if (this.state.invite === undefined) return;
        this.inviteMemberIds = ids;
        this.refreshInvite({ loading: false });
      },
      () => this.refreshInvite({ loading: false, error: msg("huddle.invite.loadFailed") }),
    );
  }

  closeInvite(): void {
    this.update({ invite: undefined });
  }

  setInviteQuery(query: string): void {
    this.refreshInvite({ query });
  }

  toggleInvitee(userId: string): void {
    const inv = this.state.invite;
    if (inv === undefined) return;
    this.refreshInvite({ picked: toggleRingPick(inv.picked, userId), rang: undefined });
  }

  /** Rings the picked people (they get a notification and can join). */
  ring(): void {
    const inv = this.state.invite;
    const huddleId = this.state.call.huddleId;
    const ring = this.deps.api.ring;
    if (inv === undefined || huddleId === undefined || ring === undefined || inv.picked.length === 0 || inv.busy) return;
    const picked = inv.picked;
    this.refreshInvite({ busy: true, error: undefined });
    ring.call(this.deps.api, this.wsId, huddleId, picked).then(
      () => this.refreshInvite({ busy: false, picked: [], rang: picked.length }),
      () => this.refreshInvite({ busy: false, error: msg("huddle.invite.failed") }),
    );
  }

  /**
   * "Call back" on a missed huddle (Slack): joins (or starts) the conversation's huddle and rings the caller, unless
   * they're in it already or the server rings them anyway (I just started a DM's huddle: it rings the other members).
   */
  async callBack(channelId: string, callerId: string): Promise<void> {
    await this.join(channelId);
    const { status, huddleId } = this.state.call;
    const h = this.state.huddle;
    const ring = this.deps.api.ring;
    if (status === "idle" || status === "leaving" || this.state.call.channelId !== channelId || huddleId === undefined || h === undefined || ring === undefined) return;
    if (callerId === this.meId || h.participants.some((p) => p.userId === callerId)) return;
    if (h.startedBy === this.meId && this.host.titleOf(channelId).isDm) return;
    await ring.call(this.deps.api, this.wsId, huddleId, [callerId]).catch(() => undefined);
  }

  private refreshInvite(patch: Partial<HuddleInviteState>): void {
    const inv = this.state.invite;
    if (inv === undefined) return;
    const next: HuddleInviteState = { ...inv, ...patch };
    for (const k of ["error", "rang"] as const) if (k in patch && patch[k] === undefined) delete next[k];
    next.candidates = ringCandidates(this.host.members(), this.inviteMemberIds, this.state.huddle, this.meId, next.query);
    const offered = new Set(next.candidates.map((c) => c.userId));
    next.picked = next.picked.filter((u) => offered.has(u));
    this.update({ invite: next });
  }

  /**
   * A realtime `notification` (WorkspaceViewModel onNotification): a huddle ring shows as an incoming call; "Ana
   * declined" (someone I rang, while I'm in that huddle) shows for a moment.
   */
  onNotification(event: NotificationEvent): void {
    const no = ringDeclined(event, this.meId);
    if (no !== undefined) {
      if (this.state.call.huddleId !== no.huddleId) return;
      const m = this.host.members()[no.userId];
      this.cancelDeclinedTimer?.();
      this.update({ declined: { userId: no.userId, name: m?.displayName ?? event.title } });
      this.cancelDeclinedTimer = this.host.clock.setTimer(DECLINED_NOTICE_MS, () => {
        this.cancelDeclinedTimer = undefined;
        this.update({ declined: undefined });
      });
      return;
    }
    const ring = incomingRing(event, this.meId, this.state.call.status === "idle" ? undefined : this.state.call.channelId);
    if (ring === undefined) return;
    this.cancelRingTimer?.();
    this.ringShownAt = this.host.clock.now();
    this.ringSeen = false;
    this.update({ incoming: ring });
    this.cancelRingTimer = this.host.clock.setTimer(RING_TIMEOUT_MS, () => {
      this.cancelRingTimer = undefined;
      this.checkRing();
    });
    this.checkRing();
  }

  /** Join the huddle I'm rung into (leaves another call first). */
  acceptRing(): Promise<void> {
    const ring = this.state.incoming;
    if (ring === undefined) return Promise.resolve();
    this.dropRing();
    return this.join(ring.channelId);
  }

  /**
   * Not now: the ring goes away and the caller hears "<me> declined" (Slack); the huddle stays open to join from the
   * conversation.
   */
  declineRing(): void {
    const ring = this.state.incoming;
    this.dropRing();
    const decline = this.deps.api.decline;
    if (ring !== undefined && decline !== undefined) decline.call(this.deps.api, this.wsId, ring.huddleId, ring.callerId).catch(() => undefined);
  }

  /**
   * × on the desktop call window: stop ringing here only. The caller isn't told (it keeps ringing for them until it
   * times out) and the huddle stays open to join from the conversation.
   */
  silenceRing(): void {
    this.dropRing();
  }

  dismissDeclined(): void {
    this.cancelDeclinedTimer?.();
    this.cancelDeclinedTimer = undefined;
    this.update({ declined: undefined });
  }

  private dropRing(): void {
    this.cancelRingTimer?.();
    this.cancelRingTimer = undefined;
    if (this.state.incoming !== undefined) this.update({ incoming: undefined });
  }

  private checkRing(): void {
    const ring = this.state.incoming;
    if (ring === undefined) return;
    const known = this.host.huddleIn(ring.channelId);
    if (known !== undefined && known.id === ring.huddleId) this.ringSeen = true;
    const inThatCall = this.state.call.status !== "idle" && this.state.call.channelId === ring.channelId;
    if (inThatCall || ringExpired(ring, this.ringShownAt, this.host.clock.now(), known, this.ringSeen, this.host.meId())) this.dropRing();
  }

  // ---------- New external huddle ----------

  /** Opens the "New external huddle" sheet, optionally in a conversation (its name becomes the title). */
  openNewMeeting(channelId?: string): void {
    const title = channelId === undefined ? "" : this.host.titleOf(channelId).title;
    this.update({ newMeeting: { title, ...(channelId === undefined ? {} : { channelId }), when: "now", time: "", open: false, busy: false } });
  }

  closeNewMeeting(): void {
    this.update({ newMeeting: undefined });
  }

  editNewMeeting(patch: Partial<NewMeetingForm>): void {
    const form = this.state.newMeeting;
    if (form === undefined || form.created !== undefined) return;
    const next: NewMeetingState = { ...form, ...patch, error: undefined };
    if ("channelId" in patch && patch.channelId === undefined) delete next.channelId;
    this.update({ newMeeting: next });
  }

  canCreateMeeting(): boolean {
    const form = this.state.newMeeting;
    return form !== undefined && !form.busy && form.created === undefined && newMeetingRequest(form, this.host.clock.now()) !== undefined;
  }

  async createMeeting(): Promise<void> {
    const form = this.state.newMeeting;
    if (form === undefined || !this.canCreateMeeting()) return;
    const req = newMeetingRequest(form, this.host.clock.now());
    if (req === undefined) return;
    this.update({ newMeeting: { ...form, busy: true, error: undefined } });
    try {
      const created = await this.deps.api.createMeeting(this.wsId, req);
      if (this.state.newMeeting !== undefined) this.update({ newMeeting: { ...this.state.newMeeting, busy: false, created } });
    } catch {
      if (this.state.newMeeting !== undefined) this.update({ newMeeting: { ...this.state.newMeeting, busy: false, error: msg("meeting.new.error") } });
    }
  }

  /**
   * "Start meeting" on a link made for now: joins its huddle here (the conversation's, or the link huddle's) and
   * returns the conversation key the call runs under. Closes the sheet. `joinHere` false: only the key (the call opens
   * in the system browser).
   */
  startMeeting(joinHere = true): string | undefined {
    const created = this.state.newMeeting?.created;
    this.closeNewMeeting();
    if (created === undefined) return undefined;
    const key = created.channelId ?? `meeting:${created.code}`;
    if (joinHere) void this.join(key);
    return key;
  }

  // ---------- Settings ----------

  openSettings(): void {
    this.update({ settingsOpen: true });
    this.deps.engine.setPreview(true);
  }

  closeSettings(): void {
    this.update({ settingsOpen: false });
    this.deps.engine.setPreview(this.previewHeld);
  }

  /** Keeps the camera preview and mic meter on outside the settings sheet (the meeting page's pre-join step). */
  holdPreview(on: boolean): void {
    if (this.previewHeld === on) return;
    this.previewHeld = on;
    this.deps.engine.setPreview(on || this.state.settingsOpen);
  }

  setNoiseSuppression(level: NoiseSuppression): void {
    this.setPrefs({ ...this.state.prefs, noiseSuppression: level });
  }

  setEchoCancellation(on: boolean): void {
    this.setPrefs({ ...this.state.prefs, echoCancellation: on });
  }

  setAutoGain(on: boolean): void {
    this.setPrefs({ ...this.state.prefs, autoGainControl: on });
  }

  setBackground(effect: BackgroundEffect): void {
    this.setPrefs({ ...this.state.prefs, background: effect });
  }

  setMirror(on: boolean): void {
    this.setPrefs({ ...this.state.prefs, mirrorVideo: on });
  }

  setJoinWithMicOff(on: boolean): void {
    this.setPrefs({ ...this.state.prefs, joinWithMicOff: on });
  }

  setJoinWithCameraOff(on: boolean): void {
    this.setPrefs({ ...this.state.prefs, joinWithCameraOff: on });
  }

  selectDevice(kind: DeviceKind, id: string): void {
    this.setPrefs(withDevice(this.state.prefs, kind, id));
  }

  testSpeaker(): void {
    this.deps.engine.playTestSound();
  }

  /** Uses a picture I picked as my background (kept on this device) and selects it. */
  async uploadBackground(file: PickedFile): Promise<void> {
    if (await this.deps.engine.setCustomBackground(file)) this.setBackground({ kind: "image", imageId: CUSTOM_BACKGROUND });
  }

  private setPrefs(prefs: HuddlePrefs): void {
    this.host.storage.set(prefsKey(this.host.prefsId()), JSON.stringify(prefs));
    this.update({ prefs });
    this.deps.engine.applyPrefs(prefs);
    this.recomputeSettings(this.deps.engine.getSnapshot());
  }

  // ---------- Internals ----------

  private dispatch(e: CallEvent): void {
    const next = callTransition(this.state.call, e);
    if (next === this.state.call) return;
    const patch: Partial<HuddleViewState> = { call: next };
    if (next.error !== undefined && next.error !== this.state.call.error) patch.error = callErrorMessage(next.error);
    this.update(patch);
    if (next.status === "idle" && e.type !== "left") {
      this.teardown();
      this.resetCall();
    }
  }

  private active(): boolean {
    const s = this.state.call.status;
    return (s === "connecting" || s === "connected" || s === "reconnecting") && this.state.call.huddleId !== undefined;
  }

  private setMe(me: CallMe): void {
    this.update({ me });
    this.recompute();
  }

  /** `settled` runs once the server answered (after its snapshot is applied) or the request failed. */
  private send(patch: UpdateHuddleRequest, settled?: () => void): void {
    const huddleId = this.state.call.huddleId;
    if (huddleId === undefined) return;
    this.deps.api
      .update(this.wsId, huddleId, patch)
      .then((h) => {
        this.myVersion = Math.max(this.myVersion, h.version);
        this.accept(h);
        settled?.();
      })
      .catch((e: unknown) => {
        settled?.();
        if (errorKind(e) === "notFound") this.dispatch({ type: "ended" });
      });
  }

  private decide(req: { guestIds?: string[]; all?: boolean; admit: boolean }): void {
    const huddleId = this.state.call.huddleId;
    if (huddleId === undefined) return;
    this.deps.api
      .decideLobby(this.wsId, huddleId, req)
      .then((h) => this.accept(h))
      .catch((e: unknown) => this.update({ error: callErrorMessage(callError(e)) }));
  }

  private moderate(req: { action: "mute"; userId: string } | { action: "remove"; userId: string } | { action: "end" }): void {
    const huddleId = this.state.call.huddleId;
    if (huddleId === undefined) return;
    this.deps.api.moderate(this.wsId, huddleId, req).catch((e: unknown) => this.update({ error: callErrorMessage(callError(e)) }));
  }

  private async updateMeeting(req: { locked?: boolean; policy?: "ask" | "open" }): Promise<Meeting | undefined> {
    const code = this.state.meeting?.code;
    if (code === undefined) return undefined;
    try {
      return await this.deps.api.updateMeeting(this.wsId, code, req);
    } catch (e) {
      this.update({ error: callErrorMessage(callError(e)) });
      return undefined;
    }
  }

  private addChat(messages: MeetingChatMessage[], mine: boolean): void {
    if (messages.length === 0) return;
    const chat = mergeChat(this.state.chat, messages, MAX_CHAT);
    const fresh = chat.length - this.state.chat.length;
    this.update({ chat, unreadChat: this.state.chatOpen || mine ? 0 : this.state.unreadChat + Math.max(fresh, 0) });
  }

  /** A newer snapshot of my huddle (REST response, event, poll). */
  private accept(h: Huddle): void {
    const cur = this.state.huddle;
    if (cur === undefined || cur.id !== h.id || h.version <= cur.version || h.id !== this.state.call.huddleId) return;
    this.update({ huddle: h });
    // Muted by a moderator: follow it (my own later unmute wins over older snapshots).
    const mine = participant(h, this.meId);
    if (mine !== undefined && mine.muted && !this.state.me.muted && h.version > this.myVersion) {
      this.setMe({ ...this.state.me, muted: true });
      this.deps.engine.setMic(false);
    }
    this.deps.engine.setRemote(h.participants.filter((p) => p.userId !== this.meId));
    this.recompute();
  }

  private scheduleHeartbeat(): void {
    this.cancelHeartbeat?.();
    this.cancelHeartbeat = this.host.clock.setTimer(HEARTBEAT_MS, () => {
      const huddleId = this.state.call.huddleId;
      if (huddleId === undefined || !this.active()) return;
      this.deps.api.heartbeat(this.wsId, huddleId).catch((e: unknown) => {
        if (errorKind(e) === "notFound") this.dispatch({ type: "ended" });
      });
      this.scheduleHeartbeat();
    });
  }

  /** In-app meeting huddles: fetch new chat now and then (the meeting page gets it with its polls instead). */
  private scheduleChatPoll(): void {
    this.cancelChatPoll?.();
    if (!this.host.heartbeats) return;
    this.cancelChatPoll = this.host.clock.setTimer(CHAT_POLL_MS, () => {
      const huddleId = this.state.call.huddleId;
      if (huddleId === undefined || !this.active()) return;
      if (this.state.meeting !== undefined) {
        this.deps.api
          .chat(this.wsId, huddleId, chatCursor(this.state.chat))
          .then((m) => this.addChat(m, false))
          .catch(() => undefined);
      }
      this.scheduleChatPoll();
    });
  }

  private onSpeech(chunk: SpeechChunk): void {
    const huddleId = this.state.call.huddleId;
    if (huddleId === undefined || !this.state.transcribing) return;
    const api = this.deps.api;
    const req =
      chunk.kind === "text"
        ? api.caption?.call(api, this.wsId, huddleId, chunk.text, captionTiming(chunk))
        : api.captionAudio?.call(api, this.wsId, huddleId, chunk.clip, chunk.mime);
    req?.then((c) => {
      if (c !== undefined) this.showCaption(c);
    }).catch(() => undefined);
  }

  private showCaption(c: HuddleCaption): void {
    if (!this.state.captionsOn) return;
    const now = this.host.clock.now();
    const captions = addCaption(this.state.captions, c, now, this.host.members());
    if (captions !== this.state.captions) this.update({ captions });
    this.scheduleCaptionExpiry(now);
  }

  private scheduleCaptionExpiry(now: number): void {
    this.cancelCaptionTimer?.();
    this.cancelCaptionTimer = undefined;
    const at = nextCaptionExpiry(this.state.captions, now);
    if (at === undefined) return;
    this.cancelCaptionTimer = this.host.clock.setTimer(Math.max(0, at - now), () => {
      this.cancelCaptionTimer = undefined;
      const t = this.host.clock.now();
      const captions = liveCaptions(this.state.captions, t);
      if (captions !== this.state.captions) this.update({ captions });
      this.scheduleCaptionExpiry(t);
    });
  }

  /**
   * Transcribe my mic while anyone has captions on and I'm unmuted: on this device when it can, else on the server
   * (core logic/transcription). The server hears which mode my device uses whenever it changes.
   */
  private syncTranscription(): void {
    const h = this.state.huddle;
    const anyone = transcribing(h) || this.state.captionsOn;
    const want = this.active() && anyone && !this.state.me.muted;
    const capability = this.deps.engine.transcriptionCapability?.();
    const choice = capability === undefined ? undefined : Transcription.selectTranscription(this.offeredModes, capability.device, capability.locale);
    const available = choice === undefined || Transcription.captionsAvailable(choice);
    const mode: HuddleTranscription | "off" = want && available ? (choice?.mode ?? this.transcription) : "off";
    if (this.state.transcribing !== want) this.update({ transcribing: want });
    const captionNote = this.state.captionsOn && choice !== undefined && capability !== undefined ? Transcription.captionNote(choice, capability.device.model) : undefined;
    if (!sameMessage(captionNote, this.state.captionNote)) this.update({ captionNote });
    if (want && choice !== undefined && !Transcription.sameTranscription(this.reportedTranscription, choice)) {
      this.reportedTranscription = choice;
      this.send({ transcription: choice });
    }
    if (mode === this.transcriptionMode) return;
    this.transcriptionMode = mode;
    this.deps.engine.setTranscription?.(mode);
  }

  /** Stops my recording, uploads it and posts it in the huddle's thread. */
  private async finishRecording(leaving: boolean): Promise<void> {
    const huddleId = this.state.call.huddleId;
    const channelId = this.state.call.channelId;
    this.cancelRecordingLimit?.();
    this.cancelRecordingLimit = undefined;
    const durationMs = Math.max(0, this.host.clock.now() - this.recordingStartedAt);
    this.update({ recording: undefined, recordingSave: "saving" });
    if (!leaving) this.send({ recording: false });
    const stop = this.deps.engine.stopRecording;
    const upload = this.host.upload;
    const post = this.deps.api.postRecording;
    try {
      const file = stop === undefined ? undefined : await stop.call(this.deps.engine);
      if (file === undefined || upload === undefined || post === undefined || huddleId === undefined || channelId === undefined) {
        this.update({ recordingSave: file === undefined ? undefined : "failed" });
        return;
      }
      const fileId = await upload.call(this.host, channelId, file);
      await post.call(this.deps.api, this.wsId, huddleId, { fileId, durationMs: Math.round(durationMs) });
      this.update({ recordingSave: "saved" });
    } catch {
      this.update({ recordingSave: "failed" });
    }
  }

  private eraseAt(x: number, y: number): void {
    const ids = strokesHit(this.liveStrokes, [x, y]);
    if (ids.length === 0) return;
    const op: HuddleDrawOp = { kind: "erase", strokeIds: ids.slice(0, 50) };
    this.liveStrokes = applyDraw(this.liveStrokes, this.meId, op, this.host.clock.now());
    this.redrawStrokes();
    this.emitDraw(op);
  }

  private endStroke(): void {
    if (this.pen === undefined) return;
    this.flushPen(true);
    this.pen = undefined;
  }

  private flushPen(done: boolean): void {
    const pen = this.pen;
    if (pen === undefined) return;
    const points = pen.pending.splice(0, 64);
    pen.flushAt = this.host.clock.now();
    if (done) {
      const s = this.liveStrokes.find((x) => x.id === pen.id);
      if (s !== undefined) {
        this.liveStrokes = applyDraw(this.liveStrokes, this.meId, { kind: "stroke", stroke: { id: pen.id, color: s.color, points: [], done: true } }, pen.flushAt);
        this.redrawStrokes();
      }
    }
    this.emitDraw({ kind: "stroke", stroke: { id: pen.id, color: this.state.drawColor, points: flatten(points), ...(done ? { done: true } : {}) } });
    if (pen.pending.length > 0) this.flushPen(done);
  }

  private emitDraw(op: HuddleDrawOp): void {
    const { channelId, huddleId } = this.state.call;
    if (channelId === undefined || huddleId === undefined) return;
    this.host.sendDraw?.(channelId, huddleId, op);
  }

  /** Publishes the strokes with their fade and schedules the next fade step. */
  private redrawStrokes(): void {
    const now = this.host.clock.now();
    this.liveStrokes = pruneStrokes(this.liveStrokes, now);
    this.update({ strokes: this.liveStrokes.map((s) => ({ ...s, opacity: strokeOpacityAt(s, now) })) });
    this.cancelStrokeTimer?.();
    this.cancelStrokeTimer = undefined;
    const at = nextStrokeTick(this.liveStrokes, now);
    if (at === undefined) return;
    this.cancelStrokeTimer = this.host.clock.setTimer(Math.max(16, at - now), () => {
      this.cancelStrokeTimer = undefined;
      this.redrawStrokes();
    });
  }

  private teardown(): void {
    this.cancelHeartbeat?.();
    this.cancelHeartbeat = undefined;
    this.cancelChatPoll?.();
    this.cancelChatPoll = undefined;
    if (this.transcriptionMode !== "off") {
      this.transcriptionMode = "off";
      this.deps.engine.setTranscription?.("off");
    }
    this.deps.engine.disconnect();
  }

  private resetCall(): void {
    this.lastLoud = {};
    this.seenReactions = {};
    this.myReaction = undefined;
    this.cancelReactionTimer?.();
    this.cancelReactionTimer = undefined;
    this.seenEffects = {};
    this.myEffect = undefined;
    this.pendingTheme = undefined;
    this.cancelEffectTimer?.();
    this.cancelEffectTimer = undefined;
    this.announced = {};
    this.sharing = false;
    this.liveStrokes = [];
    this.pen = undefined;
    this.cancelStrokeTimer?.();
    this.cancelCaptionTimer?.();
    this.update({
      captionsOn: false,
      transcribing: false,
      captions: [],
      recording: undefined,
      drawing: false,
      drawTool: "pen",
      strokes: [],
      huddle: undefined,
      media: undefined,
      me: OFF,
      tiles: [],
      stageUserId: undefined,
      speaking: [],
      columns: 1,
      meeting: undefined,
      lobby: [],
      lobbyLabel: undefined,
      isHost: false,
      moderatable: [],
      chatOpen: false,
      unreadChat: 0,
    });
  }

  private onHost(): void {
    const { channelId, huddleId } = this.state.call;
    if (channelId === undefined) return;
    if (huddleId === undefined || !this.active()) {
      this.update(this.titleFor(channelId));
      return;
    }
    const known = this.host.huddleIn(channelId);
    if (known !== undefined && known.id === huddleId) {
      this.seenInHost = true;
      const mine = this.state.huddle;
      if (participant(known, this.meId) === undefined && (mine === undefined || known.version > mine.version)) {
        this.dispatch({ type: "ended" });
        return;
      }
      if (mine === undefined || known.version > mine.version) {
        if (mine === undefined) this.update({ huddle: known });
        else this.accept(known);
      }
    } else if (this.seenInHost) {
      this.dispatch({ type: "ended" });
      return;
    }
    this.update(this.titleFor(channelId));
    this.recompute();
  }

  private titleFor(channelId: string): { title: string; isDm: boolean } {
    const meeting = this.state.huddle?.meeting;
    const t = this.host.titleOf(channelId);
    return t.title === "" && meeting !== undefined ? { title: meeting.title, isDm: true } : t;
  }

  private onEngine(): void {
    const e = this.deps.engine.getSnapshot();
    // Connection → state machine.
    if (e.connection !== "idle" && this.state.call.huddleId !== undefined) {
      this.dispatch({ type: "media", connection: e.connection });
    }
    // Errors (each reported once).
    if (e.error !== undefined && e.error.at > this.lastEngineErrorAt) {
      this.lastEngineErrorAt = e.error.at;
      const kind = e.error.kind;
      this.update({ error: callErrorMessage(kind) });
      if (kind === "micDenied" && this.active() && !this.state.me.muted) {
        this.setMe({ ...this.state.me, muted: true });
        this.send({ muted: true });
      } else if (kind === "cameraDenied" && this.active() && this.state.me.video) {
        this.setMe({ ...this.state.me, video: false });
        this.send({ video: false });
      } else if (kind === "screenDenied" && this.state.me.screen) {
        this.setMe({ ...this.state.me, screen: false });
      }
    }
    // The engine stopped recording by itself (capture lost, suspended): save what was recorded.
    const engineRecording = e.recording === true;
    if (this.engineRecording && !engineRecording && this.state.recording?.mine === true) void this.finishRecording(false);
    this.engineRecording = engineRecording;
    if (this.active()) {
      // Announce what I publish.
      if (!sameTracks(e.published, this.announced)) {
        this.announced = e.published;
        this.send({ tracks: e.published });
      }
      // Screen sharing actually started / was stopped from the system UI.
      if (e.screenSharing !== this.sharing) {
        this.sharing = e.screenSharing;
        if (e.screenSharing) this.send({ screen: true });
        else if (this.state.me.screen) {
          this.setMe({ ...this.state.me, screen: false });
          this.send({ screen: false });
        }
      }
    }
    // Speaking.
    const now = this.host.clock.now();
    this.lastLoud = updateSpeaking(this.lastLoud, { ...e.levels, [this.meId]: e.localLevel }, now);
    const speaking = speakingIds(this.lastLoud, now);
    const speakingChanged = speaking.join(",") !== this.state.speaking.join(",");
    this.update({
      speaking: speakingChanged ? speaking : this.state.speaking,
      supports: e.supports,
      preview: e.preview,
      localVideo: e.localVideo,
      localScreen: e.localScreen,
      remoteVideo: e.remoteVideo,
      remoteScreen: e.remoteScreen,
      inputLevel: levelBars(e.localLevel, LEVEL_BARS),
    });
    this.recomputeSettings(e);
    if (speakingChanged) this.recompute();
  }

  private recomputeSettings(e: CallEngineState): void {
    const prefs = this.state.prefs;
    const devices: Record<DeviceKind, DeviceOption[]> = { audioinput: [], videoinput: [], audiooutput: [] };
    for (const kind of ["audioinput", "videoinput", "audiooutput"] as const) {
      const selected = e.active[kind] ?? resolveDevice(e.devices, kind, prefs[kind]);
      devices[kind] = deviceOptions(e.devices, kind, selected);
    }
    const hasCustom = e.backgrounds[CUSTOM_BACKGROUND] !== undefined;
    const backgrounds = backgroundOptions(prefs.background, hasCustom).map((o): HuddleBackgroundOption => {
      const imageId = o.effect.kind === "image" ? o.effect.imageId : undefined;
      const thumb = imageId === undefined ? undefined : e.backgrounds[imageId];
      return thumb === undefined ? o : { ...o, thumb };
    });
    const patch: Partial<HuddleViewState> = {};
    if (!same(devices, this.state.devices)) patch.devices = devices;
    if (!same(backgrounds, this.state.backgrounds)) patch.backgrounds = backgrounds;
    this.update(patch);
  }

  /** Redraws the grid when the next floating reaction is over. */
  private scheduleReactionExpiry(now: number): void {
    const mine = this.myReaction === undefined ? undefined : this.myReaction.shownAt + HUDDLE_REACTION_MS;
    const theirs = nextReactionExpiry(this.seenReactions, now);
    const at = mine === undefined ? theirs : theirs === undefined ? mine : Math.min(mine, theirs);
    this.cancelReactionTimer?.();
    this.cancelReactionTimer = undefined;
    if (at === undefined) return;
    this.cancelReactionTimer = this.host.clock.setTimer(Math.max(0, at - now), () => {
      this.cancelReactionTimer = undefined;
      this.recompute();
    });
  }

  /** Redraws the call when the next effect's burst is over. */
  private scheduleEffectExpiry(now: number): void {
    const mine = this.myEffect === undefined ? undefined : this.myEffect.shownAt + HUDDLE_EFFECT_MS;
    const theirs = nextEffectExpiry(this.seenEffects, now);
    const at = mine === undefined ? theirs : theirs === undefined ? mine : Math.min(mine, theirs);
    this.cancelEffectTimer?.();
    this.cancelEffectTimer = undefined;
    if (at === undefined) return;
    this.cancelEffectTimer = this.host.clock.setTimer(Math.max(0, at - now), () => {
      this.cancelEffectTimer = undefined;
      this.recompute();
    });
  }

  /** Effects and theme from the snapshot (plus my own not echoed yet). */
  private recomputeEffects(h: Huddle, now: number, initial: boolean): void {
    const seen = trackEffects(this.seenEffects, h, now, initial);
    const mine = this.myEffect;
    if (mine !== undefined) {
      const echoed = seen[this.meId];
      if (echoed !== undefined && echoed.emoji === mine.emoji && echoed !== this.seenEffects[this.meId]) {
        // The server's copy of my effect: keep the burst I'm already showing.
        seen[this.meId] = { ...echoed, shownAt: mine.shownAt, seed: mine.seed };
        this.myEffect = undefined;
      } else if (now - mine.shownAt >= HUDDLE_EFFECT_MS) this.myEffect = undefined;
    }
    this.seenEffects = seen;
    const shown = this.myEffect === undefined ? seen : { ...seen, [this.meId]: this.myEffect };
    const effects = activeEffects(shown, now);
    this.scheduleEffectExpiry(now);
    if (this.pendingTheme !== undefined && (huddleTheme(h.theme)?.id ?? "none") === this.pendingTheme) this.pendingTheme = undefined;
    const theme = huddleTheme(this.pendingTheme ?? h.theme);
    const themes = themeOptions(theme?.id);
    const patch: Partial<HuddleViewState> = {};
    if (!same(effects.map((e) => e.key), this.state.effects.map((e) => e.key))) patch.effects = effects;
    if (theme?.id !== this.state.theme?.id) patch.theme = theme;
    if (!same(themes, this.state.themes)) patch.themes = themes;
    this.update(patch);
  }

  /** Grid, stage, and the meeting bits derived from the snapshot. */
  private recompute(): void {
    const h = this.state.huddle;
    if (h === undefined) return;
    const now = this.host.clock.now();
    const seen = trackReactions(this.seenReactions, h, now, this.firstSnapshot);
    const mine = this.myReaction;
    if (mine !== undefined) {
      const echoed = seen[this.meId];
      if (echoed !== undefined && echoed.emoji === mine.emoji && echoed !== this.seenReactions[this.meId]) {
        seen[this.meId] = { ...echoed, shownAt: mine.shownAt };
        this.myReaction = undefined;
      } else if (now - mine.shownAt >= HUDDLE_REACTION_MS) this.myReaction = undefined;
    }
    this.seenReactions = seen;
    this.recomputeEffects(h, now, this.firstSnapshot);
    this.firstSnapshot = false;
    const floating = activeReactions(seen, now);
    if (this.myReaction !== undefined) floating[this.meId] = this.myReaction.emoji;
    const tiles = callTiles(h, this.host.members(), this.meId, this.state.speaking, this.state.me, floating);
    this.scheduleReactionExpiry(now);
    const moderatable = h.participants.filter((p) => canModerate(h, this.meId, p.userId)).map((p) => p.userId);
    const lobby = h.lobby ?? [];
    this.update({
      tiles,
      stageUserId: stageUserId(tiles),
      columns: gridColumns(tiles.length),
      meeting: h.meeting,
      lobby: same(lobby, this.state.lobby) ? this.state.lobby : lobby,
      lobbyLabel: lobbyLabel(h),
      isHost: isHost(h, this.meId),
      moderatable: same(moderatable, this.state.moderatable) ? this.state.moderatable : moderatable,
    });
    // Recording notice (someone else's recording comes from the snapshot; mine is set locally at once).
    const notice = recordingNotice(h, this.meId, this.host.members());
    const rec = h.recording;
    if (this.state.recording?.mine !== true) {
      const next = notice === undefined || rec === undefined ? undefined : { notice, mine: rec.userId === this.meId, startedAt: rec.startedAt };
      if (!same(next, this.state.recording)) this.update({ recording: next });
    }
    // Nobody is sharing a screen any more: drawing stops.
    if (this.state.drawing && stageUserId(tiles) === undefined) this.update({ drawing: false });
    this.syncTranscription();
  }
}

function strokeOpacityAt(s: LiveStroke, now: number): number {
  return strokeOpacity(s, now);
}

function captionTiming(chunk: { words?: CaptionWord[]; ageMs?: number }): CaptionTiming | undefined {
  if (chunk.words === undefined && chunk.ageMs === undefined) return undefined;
  const t: CaptionTiming = {};
  if (chunk.words !== undefined) t.words = chunk.words;
  if (chunk.ageMs !== undefined) t.ageMs = chunk.ageMs;
  return t;
}

function sameMessage(a: MessageDescriptor | undefined, b: MessageDescriptor | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.key === b.key && JSON.stringify(a.params) === JSON.stringify(b.params);
}
